import { FocusTrap } from 'focus-trap-react';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { getAdminPanelSession } from 'app/api/cookie';
import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import NavigationLink from 'app/components/layout/AppLayout/NavigationLink';
import Box from 'app/components/v2/components/Box';
import ClickAwayListener from 'app/components/v2/components/EventListeners/ClickAwayListener';
import KeyCodeListener from 'app/components/v2/components/EventListeners/KeyCodeListener';
import styles from 'app/components/v2/constants/styles';
import { getSemanticColorVariable } from 'app/components/v2/theme/colors';
import { showBeamer } from 'app/services/beamer';
import { useGeneralDispatch, useGeneralState } from 'app/state/GeneralContext/hooks';
import { EvtKeys } from 'app/types';
import { isPayeeAndOnlyDriver } from 'app/utils/lib/user';

import AdminButton from './AdminButton';
import PayeeSelectCarrierBadge from './PayeeSelectCarrierBadge';
import {
  Container,
  StyledList,
  StyledNav,
  StyledNavProps,
} from './styledComponents';
import ToggleButton from './ToggleButton';
import { NavProps } from '..';

// Local Variables
export const closedListWidth = 60;

const getClosedWidth = (isMobileWidth: boolean) => {
  return isMobileWidth ? 0 : closedListWidth;
};

// Component Definition
const SideNav: FC<PropsWithChildren<NavProps & Omit<StyledNavProps, 'isClosedOnMobile'>>> = props => {
  const { t } = useTranslation('components');

  const user = useGetUser();
  const isDriverOnly = isPayeeAndOnlyDriver(user);
  const listRef = useRef<HTMLUListElement>(null);
  const [listWidth, setListWidth] = useState<number | string>('fit-content');

  const isAdminSession = getAdminPanelSession() === 'true';

  const generalDispatch = useGeneralDispatch();
  const {
    beamerMessageCount,
    currentDevice,
    isSideNavOpen,
  } = useGeneralState();

  const isDesktopWidth = currentDevice === 'desktop';
  const isMobileWidth = currentDevice === 'mobile';

  const handleClick = () => {
    generalDispatch({
      type: 'toggleSideNav',
    });
  };

  const handleEsc = () => {
    if (currentDevice !== 'desktop' && isSideNavOpen) {
      generalDispatch({
        type: 'toggleSideNav',
      });
    }
  };

  const handleShowBeamer = useCallback(() => {
    showBeamer(generalDispatch);
  },
  [generalDispatch]);

  const handleClickOutside = () => {
    if (isSideNavOpen && !isDesktopWidth) {
      handleClick();
    }
  };

  useEffect(() => {
    if (listRef.current) {
      const minWidth = listRef.current.clientWidth;

      const width = Math.max(minWidth, 200);

      setListWidth(width);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [listRef.current]);

  return (
    <FocusTrap
      active={!isDesktopWidth && isSideNavOpen}
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: 'legacy-full',
        },
      }}
    >
      {/* focus trap requires a single child that accepts a ref */}
      <Box
        fullHeight
        position="sticky"
        top={0}
        zIndex={styles.zIndex.drawer}
      >
        <KeyCodeListener
          keyCodeActions={[
            { action: handleEsc, key: EvtKeys.Esc },
          ]}
        >
          <ClickAwayListener onClickOutside={handleClickOutside}>
            <StyledNav
              isClosedOnMobile={!isSideNavOpen && isMobileWidth}
              topNavHeight={props.topNavHeight}
            >
              <Container
                alignItems="flex-start"
                display="flex"
                flexDirection="column"
                fullHeight
                justifyContent="space-between"
                width={`${isSideNavOpen
                  ? listWidth
                  : getClosedWidth(isMobileWidth)}px`}
              >
                <StyledList
                  ref={listRef}
                  width={listWidth}
                >
                  {props.children}
                  {!isAdminSession && !isDriverOnly && (
                    <PayeeSelectCarrierBadge />
                  )}
                </StyledList>

                <Box
                  as="ul"
                  borderTop={`2px solid ${getSemanticColorVariable('nav_border')}`}
                  fullWidth
                >
                  <AdminButton />

                  <NavigationLink
                    activePaths={[]}
                    hasNotifications={beamerMessageCount > 0}
                    iconName="updates"
                    id="updates"
                    isSideNavOpen={isSideNavOpen}
                    label={t('layout.sideNav.updatesButton', 'Updates')}
                    onClick={handleShowBeamer}
                  />

                  {!isMobileWidth && !isMobile && (
                    <ToggleButton onClick={handleClick} />
                  )}
                </Box>
              </Container>
            </StyledNav>
          </ClickAwayListener>
        </KeyCodeListener>
      </Box>
    </FocusTrap>
  );
};

export default SideNav;
